import type { VoiceError, VoiceErrorCode, VoiceRuntimeEngine } from './voice-types';

export interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

export interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message?: string;
}

export interface SpeechRecognitionInstance extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((ev: SpeechRecognitionEvent) => void) | null;
  onerror: ((ev: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
}

export function getSpeechRecognitionCtor(): (new () => SpeechRecognitionInstance) | null {
  if (typeof window === 'undefined') return null;
  const win = window as unknown as Record<string, unknown>;
  const ctor = win['SpeechRecognition'] ?? win['webkitSpeechRecognition'];
  return typeof ctor === 'function' ? (ctor as new () => SpeechRecognitionInstance) : null;
}

export function getVoiceRuntimeEngine(hasRealtime: boolean): VoiceRuntimeEngine {
  return hasRealtime ? 'web_speech' : 'media_recorder';
}

export function buildVoiceError(
  code: VoiceErrorCode,
  message: string,
  cause?: unknown,
): VoiceError {
  return { code, message, cause };
}

export function mapSpeechRecognitionError(error: string): VoiceError | null {
  if (error === 'no-speech' || error === 'aborted') return null;

  if (error === 'not-allowed') {
    return buildVoiceError(
      'permission_denied',
      'Microphone access denied. Please allow microphone in browser settings.',
    );
  }

  return buildVoiceError('transcription_failed', `Speech recognition error: ${error}`);
}

export async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i] ?? 0);
  }
  return btoa(binary);
}
